// Message protocol between the main thread and the AudioWorklet processor.
// Shared by both sides so the types stay aligned.

/** Main → worklet. */
export type MainToWorklet =
  | {
      type: "init";
      wasmBytes: ArrayBuffer;
      ringBuffer: SharedArrayBuffer;
      ringCapacity: number;
    }
  | { type: "note-on"; midi: number; velocity: number }
  | { type: "note-off"; midi: number }
  | { type: "all-notes-off" }
  | { type: "param"; name: string; value: number }
  | { type: "discrete"; name: string; value: number };

/** Worklet → main. */
export type WorkletToMain =
  | { type: "ready" }
  | { type: "error"; message: string }
  | {
      /** Periodic heartbeat reporting how many `process()` calls the
       *  worklet has run since the previous heartbeat. The main thread
       *  pairs this with its own wall-clock delta to compute the actual
       *  render rate (Chrome's AudioWorkletGlobalScope quantizes
       *  `performance.now()`, so worklet-side timing isn't reliable). */
      type: "heartbeat";
      processCallsSinceLast: number;
      quantumSize: number;
      sampleRate: number;
    };

/**
 * Ring buffer layout in the SharedArrayBuffer:
 *   - Int32Array at byte offset 0, length 4 (one slot used: write index in samples).
 *   - Float32Array immediately after, of length `RING_CAPACITY` samples.
 *
 * The worklet writes mono samples after each `process()` call and bumps the
 * write index with `Atomics.store`. Readers do an `Atomics.load` to get the
 * latest write index, then read up to that point (the samples themselves
 * are read non-atomically — visualizers tolerate occasional torn samples).
 */
export const RING_CAPACITY = 4096;
export const RING_HEADER_BYTES = 16;
export const RING_TOTAL_BYTES = RING_HEADER_BYTES + RING_CAPACITY * 4;

export function ringIndexView(sab: SharedArrayBuffer): Int32Array {
  return new Int32Array(sab, 0, 4);
}

export function ringSamplesView(sab: SharedArrayBuffer): Float32Array {
  return new Float32Array(sab, RING_HEADER_BYTES, RING_CAPACITY);
}
